import { sendPasswordReset, signInWithPassword, signUpWithPassword } from "../lib/auth.js";
import { escapeHtml } from "../lib/ui.js";

// Mínimo de la contraseña al crear cuenta. Producción exige 12 (igual que la
// política de Supabase); la base de prueba acepta menos (ver .env.staging).
const MIN_PASSWORD = Number(import.meta.env.VITE_PASSWORD_MIN_LENGTH) || 12;

export function renderLogin(container, onSuccess) {
  let mode = "login"; // "login" | "signup" | "reset"

  function template() {
    const isSignup = mode === "signup";
    const isReset = mode === "reset";
    return `
    <div class="login-screen">
      <form class="login-card" id="login-form">
        <h1>Grow Up</h1>
        <p class="subtitle">
          ${
            isReset
              ? "Te mandamos un correo con un link para elegir una contraseña nueva"
              : isSignup
                ? "Creá tu cuenta para empezar"
                : "Ingresá con tu cuenta para continuar"
          }
        </p>
        ${
          isSignup
            ? `<div class="field">
                 <label for="name">Tu nombre</label>
                 <input type="text" id="name" name="name" required autocomplete="name" />
               </div>`
            : ""
        }
        <div class="field">
          <label for="email">Correo electrónico</label>
          <input type="email" id="email" name="email" required autocomplete="email" />
        </div>
        ${
          isReset
            ? ""
            : `<div class="field">
                <label for="password">Contraseña</label>
                <input type="password" id="password" name="password" required
                       ${isSignup ? `minlength="${MIN_PASSWORD}"` : ""}
                       autocomplete="${isSignup ? "new-password" : "current-password"}" />
                ${
                  isSignup
                    ? `<p class="field-hint">Mínimo ${MIN_PASSWORD} caracteres${MIN_PASSWORD >= 12 ? `, con al menos una minúscula, una
                       mayúscula, un número y un símbolo (por ejemplo ! @ # $ %)` : ""}.</p>
                       <p class="field-hint" id="password-check" hidden></p>`
                    : ""
                }
              </div>`
        }
        <button type="submit" class="btn block" id="login-submit">
          ${isReset ? "Mandarme el link" : isSignup ? "Crear cuenta" : "Entrar"}
        </button>
        <p class="error-text" id="login-error" hidden></p>
        <p class="success-text" id="login-success" hidden></p>
        <p class="login-switch">
          ${
            isReset
              ? '<button type="button" class="link-btn" id="switch-mode">Volver a iniciar sesión</button>'
              : isSignup
                ? '¿Ya tenés cuenta? <button type="button" class="link-btn" id="switch-mode">Iniciá sesión</button>'
                : '¿Primera vez? <button type="button" class="link-btn" id="switch-mode">Creá tu cuenta</button>'
          }
        </p>
        ${
          isReset || isSignup
            ? ""
            : '<p class="login-switch"><button type="button" class="link-btn" id="forgot-password">Me olvidé la contraseña</button></p>'
        }
      </form>
    </div>
  `;
  }

  function mount() {
    container.innerHTML = template();

    const form = container.querySelector("#login-form");
    const errorEl = container.querySelector("#login-error");
    const successEl = container.querySelector("#login-success");
    const submit = container.querySelector("#login-submit");

    container.querySelector("#switch-mode").addEventListener("click", () => {
      mode = mode === "login" ? "signup" : "login";
      mount();
    });

    container.querySelector("#forgot-password")?.addEventListener("click", () => {
      mode = "reset";
      mount();
    });

    // Al crear cuenta: mostrar qué le falta a la contraseña mientras se
    // escribe, con las mismas reglas que exige Supabase. Sin esto, la persona
    // manda, Supabase rechaza, y el mensaje llega en inglés y después.
    const check = container.querySelector("#password-check");
    if (mode === "signup" && check) {
      const reglas = [
        [(v) => v.length >= MIN_PASSWORD, `${MIN_PASSWORD} caracteres`],
        ...(MIN_PASSWORD >= 12
          ? [
              [(v) => /[a-z]/.test(v), "una minúscula"],
              [(v) => /[A-Z]/.test(v), "una mayúscula"],
              [(v) => /[0-9]/.test(v), "un número"],
              [(v) => /[^A-Za-z0-9]/.test(v), "un símbolo"],
            ]
          : []),
      ];
      form.password.addEventListener("input", () => {
        const v = form.password.value;
        if (!v) { check.hidden = true; return; }
        const faltan = reglas.filter(([ok]) => !ok(v)).map(([, texto]) => texto);
        check.hidden = false;
        check.textContent = faltan.length ? `Falta: ${faltan.join(", ")}.` : "Contraseña válida ✓";
        check.style.color = faltan.length ? "var(--danger)" : "var(--success)";
      });
    }

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      errorEl.hidden = true;
      successEl.hidden = true;
      submit.disabled = true;

      const isSignup = mode === "signup";
      const isReset = mode === "reset";
      submit.textContent = isReset ? "Mandando…" : isSignup ? "Creando…" : "Entrando…";

      try {
        const email = form.email.value.trim();

        if (isReset) {
          await sendPasswordReset(email);
          successEl.textContent =
            "Listo. Si ese correo tiene cuenta, te va a llegar un link para elegir una contraseña nueva. Revisá también el spam.";
          successEl.hidden = false;
          submit.disabled = false;
          submit.textContent = "Mandarme el link";
          return;
        }

        if (isSignup) {
          const { needsEmailConfirmation } = await signUpWithPassword(
            email,
            form.password.value,
            form.name.value.trim()
          );

          if (needsEmailConfirmation) {
            successEl.textContent =
              "Cuenta creada. Revisá tu correo y confirmá la dirección para poder entrar.";
            successEl.hidden = false;
            submit.disabled = false;
            submit.textContent = "Crear cuenta";
            return;
          }
        } else {
          await signInWithPassword(email, form.password.value);
        }

        await onSuccess();
      } catch (error) {
        errorEl.textContent = translateAuthError(error.message);
        errorEl.hidden = false;
        submit.disabled = false;
        submit.textContent = isReset ? "Mandarme el link" : isSignup ? "Crear cuenta" : "Entrar";
      }
    });
  }

  mount();
}

function translateAuthError(message) {
  const map = {
    "Invalid login credentials": "Correo o contraseña incorrectos.",
    "Email not confirmed": "Todavía no confirmaste tu correo. Revisá tu bandeja de entrada.",
    "User already registered": "Ya existe una cuenta con ese correo. Iniciá sesión.",
    "Signups not allowed for this instance":
      "El registro está cerrado. Pedile a la familia que te invite desde Personas.",
  };
  // El trigger de la base rechaza altas de correos no invitados; Supabase
  // devuelve ese texto envuelto en un error genérico de base de datos.
  if (message.includes("no está invitado")) {
    return "Ese correo no está invitado. Pedile a la familia que te agregue desde Personas.";
  }
  if (map[message]) return map[message];
  if (/password/i.test(message) && /(12|at least one character)/i.test(message)) {
    return "La contraseña tiene que tener al menos 12 caracteres, con minúscula, mayúscula, número y símbolo.";
  }
  return escapeHtml(message);
}
